import React from 'react'
// UI
import {Icon as ChakraIcon, IconProps as ChakraIconProps} from '@chakra-ui/react'
// Utils
import {getIconPath} from '_factories'
import type {IconNameTypes} from '_types'

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'tile-button'

interface IconProps extends Omit<ChakraIconProps, 'name' | 'size' | 'color'> {
    name: IconNameTypes
    size?: IconSize
    color?: string
    isFilled?: boolean
    adjustXlScaling?: boolean
    onClick?: () => void
}

/**
 * Icon component wraps default chakra icon component with additional props.
 *
 * @component
 * @param {IconProps} props Properties object
 * @param {IconNameTypes} props.name Defines the icon to be rendered.
 * @param {IconSize} [props.size] Defines the icon's size. It can be 'xs', 'sm', 'md' or 'lg', 'xl, 'tile-button' default is 'lg'
 * @param {string} [props.color] Defines the icon's color. If an icon will be used as IconButton, it should have 'currentColor' as color
 * @param {boolean} [props.isFilled] Defines if the icon should be filled or just stroke
 * @param {boolean} [props.adjustXlScaling=false] Defines if icon viewBox must be adjusted for XL icon size.
 * @returns {JSX.Element} Rendered icon.
 */
const Icon = React.forwardRef<SVGSVGElement, IconProps>(
    ({name, size = 'lg', color = 'currentColor', isFilled = false, adjustXlScaling = false, onClick, ...props}, ref): JSX.Element => {
        const iconSize = `size.icon.${size}`
        const path = getIconPath(name, color, isFilled)
        const viewBox = size === 'xl' && !adjustXlScaling ? `0 0 32 32` : `0 0 24 24`
        const hasResponsiveSizes = size === 'tile-button'

        return (
            <ChakraIcon
                ref={ref}
                viewBox={viewBox}
                boxSize={{base: hasResponsiveSizes ? `${iconSize}.base` : iconSize, lg: hasResponsiveSizes ? `${iconSize}.lg` : iconSize}}
                fill="none"
                data-testid={`icon_${name}`}
                onClick={onClick}
                {...props}
            >
                {path}
            </ChakraIcon>
        )
    },
)

Icon.displayName = 'Icon'

export {Icon, IconProps}
